import 'bootstrap/dist/css/bootstrap.min.css';
import 'datatables.net';
import 'datatables.net-buttons';
import 'datatables.net-buttons-dt/css/buttons.dataTables.min.css';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons/js/buttons.print';
import $ from 'jquery';
import React, { useEffect, useRef } from 'react';

// DataTable (search / sort / paging + Copy / Excel / CSV / PDF / Print) for the
// Institute module's React-managed lists.
//
//   columns : [{ title, data, render(value, type, row) -> html string,
//                orderable, searchable, notexport }]
//             Return HTML from render(); escape user text with `esc`.
//             Any <button|a|select data-action="x" data-key="<rowKey>"> in a
//             cell is routed to onAction('x', row[, selectValue]) — no
//             per-cell React handlers needed.
//   rowKey  : field that uniquely identifies a row (used to find the row again)
//   filename: base name of the exported files
//   order   : DataTables order, default [[0,'asc']]
//   Columns marked notexport (photo, action buttons) are left out of exports.

export const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const badgeHtml = (status) => {
  const key = String(status || '').toLowerCase().replace(/\s+/g, '');
  const cls = key === 'pendingapproval' ? 'pending' : (key || 'draft');
  return `<span class="inst-badge ${cls}">${esc(status) || '—'}</span>`;
};

const InstituteDataTable = ({ data, columns, rowKey, onAction, filename = 'Mensa_Institute', order = [[0, 'asc']], emptyText }) => {
  const tableRef = useRef(null);
  const dataRef = useRef(data);
  const actionRef = useRef(onAction);
  dataRef.current = data;
  actionRef.current = onAction;

  // Build once.
  useEffect(() => {
    const el = tableRef.current;
    const exportOptions = { columns: ':visible:not(.notexport)' };
    const btn = (extend) => ({ extend, className: 'btn btn-primary dttable', filename, exportOptions });

    $(el).DataTable({
      dom: 'Blfrtip',
      pageLength: 25,
      lengthMenu: [[10, 25, 50, 100, 250, 500, -1], [10, 25, 50, 100, 250, 500, 'All']],
      buttons: [
        btn('copyHtml5'),
        { ...btn('excelHtml5'), title: null },
        btn('csvHtml5'),
        { ...btn('pdfHtml5'), orientation: 'landscape', title: filename },
        btn('print'),
      ],
      data: dataRef.current,
      order,
      language: { emptyTable: emptyText || 'No data available' },
      columns: columns.map((c) => ({
        title: c.title,
        data: c.data === undefined ? null : c.data,
        render: c.render,
        orderable: c.orderable !== false,
        searchable: c.searchable !== false,
        className: c.notexport ? 'notexport' : undefined,
      })),
      initComplete: () => { $('.dt-length select').addClass('form-select form-select-sm'); },
    });

    const findRow = (key) => dataRef.current.find((r) => String(r[rowKey]) === String(key));

    $(el).on('click', '[data-action]:not(select)', function (e) {
      e.preventDefault();
      const row = findRow($(this).data('key'));
      if (row) actionRef.current?.($(this).data('action'), row);
    });
    $(el).on('change', 'select[data-action]', function () {
      const row = findRow($(this).data('key'));
      const value = $(this).val();
      $(this).val('');
      if (row && value) actionRef.current?.($(this).data('action'), row, value);
    });

    return () => {
      $(el).off();
      if ($.fn.DataTable.isDataTable(el)) $(el).DataTable().destroy();
    };
    // eslint-disable-next-line
  }, []);

  // Refresh rows in place when data changes (keeps page / search / sort).
  useEffect(() => {
    const el = tableRef.current;
    if (!$.fn.DataTable.isDataTable(el)) return;
    const t = $(el).DataTable();
    t.clear();
    t.rows.add(data);
    t.draw(false);
  }, [data]);

  return (
    <div className="inst-table-scroll">
      <table ref={tableRef} className="table table-striped table-bordered" style={{ width: '100%' }}></table>
    </div>
  );
};

export default InstituteDataTable;
